import { apiClient } from '@/lib/api'
import {
  StandardResponse,
  PaginatedStandardResponse,
  MessageResponse,
  extractData,
  extractPaginatedData,
} from '@/types/api-response'

// ============================================================================
// Backend DTOs (Mapped from backend structs)
// ============================================================================

/**
 * Tenant status enum
 */
export type TenantStatus = 'Active' | 'Suspended' | 'Pending'

/**
 * Tenant type enum
 */
export type TenantType = 'Individual' | 'Business' | 'Enterprise'

/**
 * Tenant role enum
 */
export type TenantRole = 'Owner' | 'Admin' | 'Manager' | 'User' | 'Guest'

/**
 * Member status enum
 */
export type MemberStatus = 'Active' | 'Pending' | 'Suspended'

/**
 * TenantListDto - List item representation
 */
/** Where one tenant stands on one platform commitment. Mirrors TenantAgreementStatusDto. */
export interface TenantAgreementStatusDto {
  type: string
  /**
   * "NotApplicable" | "Accepted" | "Outstanding".
   *
   * NotApplicable means nobody has been asked — the commitment's flag is off or nothing has been
   * published — and must never be rendered as "not agreed".
   *
   * Accepted means SOMEBODY in the workspace signed the revision being asked for. The gate is per
   * person, so a member who joined afterwards is still blocked while this reads Accepted; the
   * wording must not claim the whole workspace has agreed.
   */
  state: 'NotApplicable' | 'Accepted' | 'Outstanding'
  acceptedVersion?: number | null
  acceptedAt?: string | null
  acceptedByEmail?: string | null
}

export interface TenantListDto {
  id: string
  name: string
  slug: string
  type: TenantType
  status: TenantStatus
  ownerId: string
  ownerEmail: string
  ownerFullName: string
  ownerPhoneNumber?: string | null
  memberCount: number
  resourceCount: number
  balance?: number
  currency?: string
  /** Sign-up bonus credit still usable, in `bonusCurrency`. A pool of its own — it may only fund a
   *  trial VM, so it is never part of `balance` and the two are never summed. */
  bonusCreditBalance?: number
  /** Currency of the bonus lot; falls back to `currency` when the tenant holds no lot. */
  bonusCurrency?: string
  /**
   * Lifecycle state of the tenant's bonus lot, or null when they have never had one. It is what
   * tells a `bonusCreditBalance` of 0 apart: never claimed, spent, expired or revoked — and the
   * balance screen reads it to decide whether the bonus figure can be edited at all.
   */
  bonusStatus?: 'Active' | 'Exhausted' | 'Expired' | 'Revoked' | null
  /**
   * When the bonus lot expires (ISO 8601, UTC), or null when there is no lot. A lot can still read
   * `Active` after this has passed — the daily sweep is what moves it to `Expired` — so both are
   * needed to know whether the credit is really usable.
   */
  bonusExpiresAt?: string | null
  createdAt: string
  updatedAt?: string
  /** One entry per platform commitment; empty while the feature has never been configured. */
  agreements?: TenantAgreementStatusDto[]
}

/**
 * TenantDetailDto - Full tenant details
 */
export interface TenantDetailDto {
  id: string
  name: string
  slug: string
  type: TenantType
  status: TenantStatus
  ownerId: string
  ownerEmail: string
  ownerFullName: string
  subscriptionPlanId?: string
  subscriptionPlanName?: string
  quotas: TenantQuotasDto
  currentUsage: TenantQuotasDto
  settings: string
  billingAccountId?: string
  memberCount: number
  resourceCount: number
  /**
   * Always null today: GetTenantDetailsQueryHandler hardcodes it pending a repository
   * method. Kept to mirror the backend DTO — read the balance from the billing summary
   * (`availableBalance`, already converted to the major unit) instead.
   */
  accountBalance?: number // Smallest currency unit (renamed from accountBalanceCents)
  billingStatus: string
  createdAt: string
  updatedAt?: string
}

/**
 * TenantQuotasDto - Tenant resource quotas
 * Supports both detailed backend structure and simplified frontend aliases
 */
export interface TenantQuotasDto {
  maxVms?: number
  maxCpuCores?: number
  maxRamGb?: number
  maxStorageGb?: number
  maxUsers?: number
  maxSnapshots?: number
  // Frontend aliases via JSON property names
  maxVirtualMachines?: number  // alias for maxVms
  maxStorage?: number          // alias for maxStorageGb
  maxBandwidth?: number | null // computed, currently null
}

/**
 * TenantMemberDto - Tenant member information
 */
export interface TenantMemberDto {
  id: string
  userId: string
  email: string
  fullName: string
  role: TenantRole
  status: MemberStatus
  joinedAt: string
  invitedBy?: string
  inviterEmail?: string
}

/**
 * TenantResourceSummaryDto - Aggregated resource summary
 */
export interface TenantResourceSummaryDto {
  totalResources: number
  vmCount: number
  storageCount: number
  networkCount: number
  totalCpuCores: number
  totalRamGb: number
  totalStorageGb: number
  totalMonthlyCost: number // Smallest currency unit (renamed from totalMonthlyCostCents)
  resources: ResourceItemDto[]
}

/**
 * ResourceItemDto - Individual resource item
 */
export interface ResourceItemDto {
  id: string
  name: string
  type: string
  status: string
  region: string
  cpuCores?: number
  memoryGB?: number
  storageGB?: number
  ipAddress?: string
  monthlyCost: number // Smallest currency unit (renamed from monthlyCostCents)
  createdAt: string
}

/**
 * InvoiceSummaryDto - Summary of a single invoice
 */
export interface InvoiceSummaryDto {
  id: string
  invoiceNumber: string
  status: string
  total: number
  amountDue: number
  issueDate: string
  dueDate?: string
}

/**
 * PaymentSummaryDto - Summary of a single payment
 */
export interface PaymentSummaryDto {
  id: string
  amount: number
  paymentMethod: string
  status: string
  createdAt: string
}

/**
 * TenantBillingSummaryDto - Complete billing summary for tenant
 * Supports both detailed backend fields and frontend-compatible computed aliases
 */
export interface TenantBillingSummaryDto {
  tenantId: string
  availableBalance: number
  pendingBalance: number
  creditBalance: number
  reservedBalance: number
  currency: string
  totalInvoices: number
  paidInvoices: number
  pendingInvoices: number
  overdueInvoices: number
  totalInvoiced: number
  totalPaid: number
  totalOutstanding: number
  totalPayments: number
  recentInvoices: InvoiceSummaryDto[]
  recentPayments: PaymentSummaryDto[]
  // Frontend-compatible aliases
  totalSpent?: number        // alias for totalPaid
  currentBalance?: number    // alias for availableBalance
  lastPaymentDate?: string   // computed from recentPayments
}

/**
 * UsageDataPointDto - Single usage data point
 */
export interface UsageDataPointDto {
  timestamp: string
  resourceCount: number
  cpuCoresUsed: number
  ramGbUsed: number
  storageGbUsed: number
  spending: number // Smallest currency unit (renamed from spendingCents)
}

/**
 * UsageSummaryDto - Summary statistics
 */
export interface UsageSummaryDto {
  averageResourceCount: number
  peakResourceCount: number
  averageCpuCores: number
  peakCpuCores: number
  averageRamGb: number
  peakRamGb: number
  averageStorageGb: number
  peakStorageGb: number
  totalSpending: number // Smallest currency unit (renamed from totalSpendingCents)
  averageDailySpending: number // Smallest currency unit (renamed from averageDailySpendingCents)
}

/**
 * TenantUsageHistoryDto - Complete usage history
 */
export interface TenantUsageHistoryDto {
  tenantId: string
  startDate: string
  endDate: string
  granularity: string
  dataPoints: UsageDataPointDto[]
  summary: UsageSummaryDto
}

// ============================================================================
// Request DTOs
// ============================================================================

export interface CreateTenantDto {
  name: string
  slug: string
  type: TenantType
  ownerId: string
  subscriptionPlanId?: string
  quotas?: TenantQuotasDto
  settings?: string
}

export interface UpdateTenantDto {
  name?: string
  type?: TenantType
  subscriptionPlanId?: string
  settings?: string
}

export interface UpdateTenantQuotasDto {
  maxVms?: number
  maxCpuCores?: number
  maxRamGb?: number
  maxStorageGb?: number
  maxUsers?: number
  maxSnapshots?: number
}

export interface SuspendTenantRequest {
  reason: string
}

export interface AdjustTenantBalanceDto {
  /** Read only when `amount` is given. */
  adjustmentType: 'set' | 'add' | 'deduct'
  /** Omit to leave the cash wallet alone and adjust only `bonusRemainingAmount`. */
  amount?: number
  /**
   * The remainder the tenant's sign-up bonus lot should be left holding, in the lot's own
   * currency. Absolute, never a delta. Omit to leave the bonus alone.
   *
   * It rewrites an existing lot only — a tenant with no lot, or one expired or revoked, comes back
   * BONUS_LOT_NOT_EDITABLE. Minting a lot is the Sign-up Bonus screen's job.
   */
  bonusRemainingAmount?: number
  /** Optional. Operator copy, recorded in the audit metadata, never shown to the tenant. */
  reason?: string
}

export interface TenantBalanceAdjustedDto {
  tenantId: string
  previousBalance: number
  /** Null when the request left the cash wallet alone and only rewrote the bonus. */
  adjustedAmount: number | null
  /** Where the wallet now stands — unchanged from `previousBalance` on a bonus-only edit. */
  newBalance: number
  adjustmentType: string
  reason: string
  adjustedAt: string
  adjustedBy: string
  /** Bonus remainder the lot now holds. Null when the bonus was not touched. */
  newBonusBalance: number | null
  /** Currency of the bonus lot. Null when the bonus was not touched. */
  bonusCurrency: string | null
}

// ============================================================================
// Query/Filter Parameters
// ============================================================================

/**
 * Filters the tenant list UI can actually produce.
 *
 * No `type` here on purpose: the field existed and was serialized to `typeFilter`, but no
 * control ever set it, so it was a dead branch. The backend still accepts `typeFilter`,
 * `createdFromFilter` and `createdToFilter` on the endpoint — add them back here together
 * with the control that sets them, not before.
 */
export interface ListTenantsParams {
  page?: number
  limit?: number
  search?: string
  status?: TenantStatus
  /** Created-at range lower bound, ISO date. Sent as createdFromFilter. */
  createdFrom?: string
  /** Created-at range upper bound, ISO date. Sent as createdToFilter. */
  createdTo?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface GetTenantMembersParams {
  page?: number
  limit?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

// ============================================================================
// API Service
// ============================================================================

/**
 * Admin Tenant Service
 *
 * All methods return extracted data from StandardResponse<T> or PaginatedStandardResponse<T>
 * using extractData() and extractPaginatedData() helper functions.
 *
 * Error handling: Throws Error if response is an ErrorResponse
 */
export const adminTenantService = {
  /**
   * List tenants with pagination and filters
   * @param params Query parameters (page, limit, search, status, created-at range, sortBy, sortOrder)
   * @param signal Abort signal supplied by TanStack Query, so a superseded request is cancelled
   * @returns List of tenants with pagination info
   */
  async listTenants(
    params?: ListTenantsParams,
    signal?: AbortSignal
  ): Promise<{ data: TenantListDto[]; pagination: any }> {
    // Map the frontend params to the backend GetTenantsQuery field names
    // (nameFilter / pageNumber / pageSize / statusFilter / sortDirection).
    // The admin tenants endpoint binds [FromQuery] GetTenantsQuery by property name, so
    // sending search/page/limit was silently ignored and always returned all tenants.
    const query: Record<string, string | number> = {}
    if (params) {
      if (params.page != null) query.pageNumber = params.page
      if (params.limit != null) query.pageSize = params.limit
      // Trimmed here as well as in the filter component: a whitespace-only term is not a
      // filter, and the backend would otherwise match the surrounding spaces literally.
      const search = params.search?.trim()
      if (search) query.nameFilter = search
      if (params.status) query.statusFilter = params.status
      if (params.createdFrom) query.createdFromFilter = params.createdFrom
      if (params.createdTo) query.createdToFilter = params.createdTo
      if (params.sortBy) query.sortBy = params.sortBy
      if (params.sortOrder) query.sortDirection = params.sortOrder
    }
    const response = await apiClient.get<
      PaginatedStandardResponse<TenantListDto>
    >('/api/v1/admin/tenants', {
      params: query,
      signal,
    })
    return extractPaginatedData(response.data)
  },

  /**
   * Get tenant detail by ID
   * @param tenantId The tenant ID
   * @returns Tenant detail with quotas, usage, billing, etc.
   */
  async getTenantDetail(tenantId: string): Promise<TenantDetailDto> {
    const response = await apiClient.get<StandardResponse<TenantDetailDto>>(
      `/api/v1/admin/tenants/${tenantId}`
    )
    return extractData(response.data)
  },

  /**
   * Create new tenant (admin-initiated)
   * @param data Tenant creation data
   * @returns Created tenant detail
   */
  async createTenant(data: CreateTenantDto): Promise<TenantDetailDto> {
    const response = await apiClient.post<StandardResponse<TenantDetailDto>>(
      '/api/v1/admin/tenants',
      data
    )
    return extractData(response.data)
  },

  /**
   * Update tenant information
   * @param tenantId The tenant ID to update
   * @param data Update data (name, type, subscriptionPlanId, settings)
   * @returns Updated tenant detail
   */
  async updateTenant(tenantId: string, data: UpdateTenantDto): Promise<TenantDetailDto> {
    const response = await apiClient.put<StandardResponse<TenantDetailDto>>(
      `/api/v1/admin/tenants/${tenantId}`,
      data
    )
    return extractData(response.data)
  },

  /**
   * Suspend a tenant (stops all resources and blocks access)
   * @param tenantId The tenant ID to suspend
   * @param request Suspension request with reason
   * @returns Success message
   */
  async suspendTenant(tenantId: string, request: SuspendTenantRequest): Promise<void> {
    const response = await apiClient.post<MessageResponse>(
      `/api/v1/admin/tenants/${tenantId}/suspend`,
      request
    )
    // MessageResponse only has success and message fields, no data to extract
    if (!response.data.success) {
      throw new Error('Failed to suspend tenant')
    }
  },

  /**
   * Activate a suspended tenant (restores access)
   * @param tenantId The tenant ID to activate
   * @returns Success message
   */
  async activateTenant(tenantId: string): Promise<void> {
    const response = await apiClient.post<MessageResponse>(
      `/api/v1/admin/tenants/${tenantId}/activate`
    )
    if (!response.data.success) {
      throw new Error('Failed to activate tenant')
    }
  },

  /**
   * Update tenant quotas
   * @param tenantId The tenant ID
   * @param data Quota update data
   * @returns Updated tenant detail
   */
  async updateTenantQuotas(
    tenantId: string,
    data: UpdateTenantQuotasDto
  ): Promise<TenantDetailDto> {
    const response = await apiClient.put<StandardResponse<TenantDetailDto>>(
      `/api/v1/admin/tenants/${tenantId}/quotas`,
      data
    )
    return extractData(response.data)
  },

  /**
   * Get list of tenant members with roles
   * @param tenantId The tenant ID
   * @param params Optional pagination and filter parameters
   * @returns List of tenant members
   */
  async getTenantMembers(
    tenantId: string,
    params?: GetTenantMembersParams
  ): Promise<TenantMemberDto[]> {
    const response = await apiClient.get<StandardResponse<TenantMemberDto[]>>(
      `/api/v1/admin/tenants/${tenantId}/members`,
      { params }
    )
    return extractData(response.data)
  },

  /**
   * Get tenant's resource summary
   * @param tenantId The tenant ID
   * @returns Tenant resource summary with metrics
   */
  async getTenantResources(tenantId: string): Promise<TenantResourceSummaryDto> {
    const response = await apiClient.get<StandardResponse<TenantResourceSummaryDto>>(
      `/api/v1/admin/tenants/${tenantId}/resources`
    )
    return extractData(response.data)
  },

  /**
   * Get tenant's billing summary (balance, invoices, payments)
   * @param tenantId The tenant ID
   * @returns Tenant billing summary with recent invoices and payments
   */
  async getTenantBillingSummary(tenantId: string): Promise<TenantBillingSummaryDto> {
    const response = await apiClient.get<StandardResponse<TenantBillingSummaryDto>>(
      `/api/v1/admin/tenants/${tenantId}/billing-summary`
    )
    return extractData(response.data)
  },

  /**
   * Get tenant's usage history over time
   * @param tenantId The tenant ID
   * @param startDate Start date (optional, defaults to 1 month ago)
   * @param endDate End date (optional, defaults to now)
   * @param granularity Granularity: 'day', 'week', or 'month' (default: 'day')
   * @returns Tenant usage history with data points and summary
   */
  async getTenantUsageHistory(
    tenantId: string,
    startDate?: string,
    endDate?: string,
    granularity?: string
  ): Promise<TenantUsageHistoryDto> {
    const response = await apiClient.get<StandardResponse<TenantUsageHistoryDto>>(
      `/api/v1/admin/tenants/${tenantId}/usage-history`,
      {
        params: {
          startDate,
          endDate,
          granularity,
        },
      }
    )
    return extractData(response.data)
  },

  /**
   * Sends this tenant an expiry reminder listing every machine within seven days of expiry.
   *
   * Which machines qualify is decided entirely by the API — the console must not second-guess
   * it, or an operator would be shown a Send that does nothing or hidden one that would have
   * worked.
   */
  async sendExpiryReminder(tenantId: string): Promise<SendExpiryReminderResultDto> {
    const response = await apiClient.post<StandardResponse<SendExpiryReminderResultDto>>(
      `/api/v1/admin/tenants/${tenantId}/send-expiry-reminder`
    )
    return extractData(response.data)
  },

  /**
   * Adjust tenant account balance (Admin operation)
   * @param tenantId The tenant ID
   * @param data Adjustment payload (adjustmentType, amount, reason)
   * @returns Adjusted balance details
   */
  async adjustTenantBalance(
    tenantId: string,
    data: AdjustTenantBalanceDto
  ): Promise<TenantBalanceAdjustedDto> {
    const response = await apiClient.post<StandardResponse<TenantBalanceAdjustedDto>>(
      `/api/v1/admin/tenants/${tenantId}/adjust-balance`,
      data
    )
    return extractData(response.data)
  },
}

/**
 * What a manual expiry-reminder send did.
 *
 * `sent: false` is a success, not a failure: it means no running or stopped machine of this tenant
 * expires within the next seven days, so there was nothing to remind them about.
 */
export interface SendExpiryReminderResultDto {
  /** Whether a reminder went out on at least one channel. */
  sent: boolean
  /** Whether an email was queued. */
  emailSent: boolean
  /** Whether an in-app notification was written. */
  inAppSent: boolean
  /** Machines listed in the email. Zero when nothing was due. */
  resourceCount: number
  /** Days left on the nearest machine, or null when nothing was due. */
  soonestDays: number | null
  /** Where the email went, or null when no email was sent. */
  recipientEmail: string | null
  /** A sentence written by the API, shown to the operator as-is. */
  message: string
}
